import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { GamePhase, GameSessionState, DismissalInterruptData } from '../interfaces/game-state.interface';
import { GameQuestion, QuizAnswer, QuizSession, QuizSubject } from '../interfaces/question.interface';
import { ClassCreature } from '../interfaces/creature.interface';
import { QuestionBankService } from './question-bank.service';
import { ScoringService } from './scoring.service';
import { GameStatePersistenceService } from './game-state-persistence.service';
import { QUESTIONS_PER_SESSION, getLevelFromXP, getLeagueTier } from '../data/league-config';
import { AmplitudeService } from 'src/app/_services/amplitude.service';

@Injectable({ providedIn: 'root' })
export class GameModeService {

  // Game phase
  private _gamePhase = new BehaviorSubject<GamePhase>(GamePhase.IDLE);
  gamePhase$ = this._gamePhase.asObservable();

  // Quiz state
  private _currentQuestion = new BehaviorSubject<GameQuestion | null>(null);
  currentQuestion$ = this._currentQuestion.asObservable();

  private _quizSession = new BehaviorSubject<QuizSession | null>(null);
  quizSession$ = this._quizSession.asObservable();

  // Creature
  private _creature = new BehaviorSubject<ClassCreature | null>(null);
  creature$ = this._creature.asObservable();

  // Dismissal interrupt
  private _dismissalInterrupt = new BehaviorSubject<DismissalInterruptData | null>(null);
  dismissalInterrupt$ = this._dismissalInterrupt.asObservable();

  // Timer
  private _timerValue = new BehaviorSubject<number>(0);
  timerValue$ = this._timerValue.asObservable();

  // Available classes (for class selection screen)
  private _availableClasses = new BehaviorSubject<any[]>([]);
  availableClasses$ = this._availableClasses.asObservable();

  // Level up event
  private _levelUp = new BehaviorSubject<{ oldLevel: number; newLevel: number } | null>(null);
  levelUp$ = this._levelUp.asObservable();

  // Last answer result
  private _lastAnswerResult = new BehaviorSubject<{ isCorrect: boolean; xpEarned: number; streak: number } | null>(null);
  lastAnswerResult$ = this._lastAnswerResult.asObservable();

  private timerInterval: any;
  private currentClassId: number | null = null;
  private pausedPhase: GamePhase | null = null;

  // Phase 1: auto-start game cycle on first entry
  autoStartOnEntry = true;

  constructor(
    private questionBankService: QuestionBankService,
    private scoringService: ScoringService,
    private persistenceService: GameStatePersistenceService,
    private amplitudeService: AmplitudeService
  ) {}

  // --- Phase Management ---

  setPhase(phase: GamePhase): void {
    this._gamePhase.next(phase);
  }

  getPhase(): GamePhase {
    return this._gamePhase.getValue();
  }

  isGameActive(): boolean {
    return this._gamePhase.getValue() !== GamePhase.IDLE;
  }

  setAvailableClasses(classes: any[]): void {
    this._availableClasses.next(classes);
  }

  getAvailableClasses(): any[] {
    return this._availableClasses.getValue();
  }

  openClassSelect(): void {
    this.setPhase(GamePhase.CLASS_SELECT);
  }

  // Phase 1: skip class select + student confirm — auto-pick selected class or first class
  quickStart(selectedClassId?: number): void {
    const classes = this._availableClasses.getValue();
    if (classes.length > 0) {
      const selectedClass = selectedClassId
        ? classes.find(c => c.id === selectedClassId)
        : null;
      this.currentClassId = selectedClass ? selectedClass.id : classes[0].id;
      this.setPhase(GamePhase.STUDENT_LOADING);
    }
  }

  selectClassAndLoadStudents(classId: number): void {
    this.currentClassId = classId;
    this.setPhase(GamePhase.STUDENT_LOADING);
  }

  proceedToHub(classId: number): void {
    this.openGameHub(classId);
  }

  openGameHub(classId: number, gradeId?: number): void {
    this.currentClassId = classId;
    const creature = this.persistenceService.getCreature(classId);
    this._creature.next(creature);

    // Phase 1: skip creature pick + hub, go straight to subject pick
    this.setPhase(GamePhase.SUBJECT_PICK);
  }

  goToGameSelect(subject: any): void {
    this.setPhase(GamePhase.GAME_SELECT);
  }

  closeGame(): void {
    this.stopTimer();
    this._quizSession.next(null);
    this._currentQuestion.next(null);
    this._lastAnswerResult.next(null);
    this.autoStartOnEntry = true; // Reset for next entry
    this.setPhase(GamePhase.IDLE);
  }

  // --- Creature Management ---

  selectCreature(creatureTypeId: string, customName: string, className?: string): void {
    if (!this.currentClassId) return;
    const creature = this.persistenceService.createNewCreature(
      this.currentClassId, creatureTypeId, customName, className
    );
    this._creature.next(creature);
    this.setPhase(GamePhase.HUB);
  }

  getCreature(): ClassCreature | null {
    return this._creature.getValue();
  }

  getCurrentClassId(): number | null {
    return this.currentClassId;
  }

  setCurrentClassId(classId: number): void {
    this.currentClassId = classId;
  }

  /**
   * Human-readable name of the currently-selected class (e.g. "4A").
   * Class objects carry `.name` (mock + real `school_class`). Null pre-selection.
   */
  getCurrentClassName(): string | null {
    return this.getAvailableClasses().find(c => c.id === this.currentClassId)?.name ?? null;
  }

  /**
   * Human-readable name of the selected class's grade (e.g. "Grade 4").
   * Class objects carry `.grade.name` (mock + real `school_class`). Null pre-selection.
   */
  getCurrentGradeName(): string | null {
    return this.getAvailableClasses().find(c => c.id === this.currentClassId)?.grade?.name ?? null;
  }

  // --- Quiz Management ---

  startQuiz(subject: QuizSubject | 'random', gradeLevel?: number): void {
    const questions = this.questionBankService.getQuestions({
      subject,
      gradeLevel,
      count: QUESTIONS_PER_SESSION
    });

    if (questions.length === 0) return;

    const session: QuizSession = {
      questions,
      currentIndex: 0,
      answers: [],
      subject,
      startedAt: new Date()
    };

    this._quizSession.next(session);
    this._currentQuestion.next(questions[0]);
    this._lastAnswerResult.next(null);
    this.setPhase(GamePhase.QUIZ);
    this.startTimer(questions[0].timeLimit);
  }

  submitAnswer(optionId: 'A' | 'B' | 'C' | 'D' | null): void {
    const session = this._quizSession.getValue();
    const question = this._currentQuestion.getValue();
    if (!session || !question || !this.currentClassId) return;

    this.stopTimer();
    const timeRemaining = this._timerValue.getValue();
    const isCorrect = optionId === question.correctOptionId;

    // Update streak
    const creature = this.persistenceService.updateStreak(this.currentClassId, isCorrect);
    const streak = creature?.currentStreak || 0;

    // Calculate XP
    const score = this.scoringService.calculateXP({
      difficulty: question.difficulty,
      streak,
      questionSubject: question.subject,
      timeRemaining,
      timeLimit: question.timeLimit,
      isCorrect
    });

    // Update XP
    const oldLevel = creature?.level || 0;
    if (score.totalXP > 0) {
      const updated = this.persistenceService.updateXP(this.currentClassId, score.totalXP);
      if (updated) {
        this._creature.next(updated);
        if (updated.level > oldLevel) {
          this._levelUp.next({ oldLevel, newLevel: updated.level });
        }
      }
    } else if (creature) {
      this._creature.next(creature);
    }

    // Record answer
    const answer: QuizAnswer = {
      questionId: question.id,
      selectedOptionId: optionId,
      isCorrect,
      timeRemaining,
      xpEarned: score.totalXP
    };
    session.answers.push(answer);
    session.currentIndex++;

    this._quizSession.next(session);
    this._lastAnswerResult.next({ isCorrect, xpEarned: score.totalXP, streak });
    this.setPhase(GamePhase.QUIZ_RESULT);
  }

  nextQuestion(): void {
    const session = this._quizSession.getValue();
    if (!session) return;

    if (session.currentIndex >= session.questions.length) {
      this.amplitudeService.trackEvent('gameWorld-classicQuiz-finished', {});
      this.setPhase(GamePhase.QUIZ_SUMMARY);
      return;
    }

    const nextQ = session.questions[session.currentIndex];
    this._currentQuestion.next(nextQ);
    this._lastAnswerResult.next(null);
    this.setPhase(GamePhase.QUIZ);
    this.startTimer(nextQ.timeLimit);
  }

  finishQuiz(): void {
    this._quizSession.next(null);
    this._currentQuestion.next(null);
    this._lastAnswerResult.next(null);
    this._levelUp.next(null);
    // Phase 1: no HUB, go back to subject pick
    this.setPhase(GamePhase.SUBJECT_PICK);
  }

  // --- Timer ---

  private startTimer(seconds: number): void {
    this.stopTimer();
    this._timerValue.next(seconds);
    this.timerInterval = setInterval(() => {
      const current = this._timerValue.getValue();
      if (current <= 0) {
        this.stopTimer();
        this.submitAnswer(null); // Time's up
      } else {
        this._timerValue.next(current - 1);
      }
    }, 1000);
  }

  private stopTimer(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  /** Public method to stop the quiz timer from outside (e.g. when navigating away) */
  stopQuizTimer(): void {
    this.stopTimer();
  }

  // --- Dismissal Interrupt ---

  triggerDismissalInterrupt(data: DismissalInterruptData): void {
    if (!this.isGameActive()) return;

    this.pausedPhase = this.getPhase();
    if (this.getPhase() === GamePhase.QUIZ) {
      this.stopTimer();
    }
    this._dismissalInterrupt.next(data);
  }

  dismissDismissalInterrupt(): void {
    this._dismissalInterrupt.next(null);
    if (this.pausedPhase === GamePhase.QUIZ) {
      const remaining = this._timerValue.getValue();
      if (remaining > 0) {
        this.startTimer(remaining);
      }
    }
    this.pausedPhase = null;
  }

  // --- Cleanup ---

  destroy(): void {
    this.stopTimer();
    this._gamePhase.next(GamePhase.IDLE);
    this._currentQuestion.next(null);
    this._quizSession.next(null);
    this._dismissalInterrupt.next(null);
    this._lastAnswerResult.next(null);
    this._levelUp.next(null);
    this._availableClasses.next([]);
    this.autoStartOnEntry = true;
  }
}
